import { Component, useEffect, useRef, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import styles from "./Navbar.module.css";
import { FaChevronDown, FaChevronRight, FaUser } from "react-icons/fa";
import { FiUser } from "react-icons/fi";
import { useAuth } from "@/Common/Auth/AuthContext";
import LogoutModal from "@/Component/Module/Profile/LogoutModal";
import { clearAuthSession, getNavUserName, withRegionPath } from "@/utils/session";
import { isIndianUser } from "@/utils/getCountryCode";
import { buildCountrySwitchEntryUrl, filterNavItemsForIndia, getRegionalHomeHref, REGION_OPTIONS, setRegionPreference } from "@/utils/region";
import ModuleNav from "../ModuleNav";
import { MdOutlineArrowOutward } from "react-icons/md";
import AccountMenu from "./AccountMenu";
import { IoMenu } from "react-icons/io5";
import useBodyScrollLock from "@/hooks/useBodyScrollLock";
import NotificationDropdown from "./NotificationDropdown";

class NotificationSafe extends Component {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  render() {
    return this.state.failed ? null : this.props.children;
  }
}

const MENU_ITEMS = [
  { label: "Holidays", href: "/holiday" },
  { label: "Stays", href: "/stays" },
  { label: "Flights", href: "/flights" },
  { label: "Activities", href: "/activity" },
  { label: "Cart", href: "/cart" },
  { label: "Download App", href: "/download-app" },
  { label: "Testimonials", href: "/testimonials" },
  { label: "Blogs", href: "/blog" },
  { label: "About Us", href: "/about-us" },
  { label: "Contact Us", href: "/contact-us" },
  // { label: "Deals", href: "/deals" },
  // { label: "Coupons", href: "/coupons" },
];

const DRAWER_AWARDS = [
  { label: "Award 1", src: "/assets/images/Award1.png" },
  { label: "Award 2", src: "/assets/images/Award2.png" },
];

export default function Navbar({ showModuleNav = false }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [accountPopoverOpen, setAccountPopoverOpen] = useState(false);
  const [localeOpen, setLocaleOpen] = useState(false);
  const [logoutOpen, setLogoutOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const accountMenuRef = useRef(null);
  const localeRef = useRef(null);

  const { openAuthModal, loggedIn } = useAuth();
  const navUserName = getNavUserName();
  const loginLabel = loggedIn ? `Hi, ${navUserName}` : "Login";
  const isIndia = isIndianUser();
  const selectedRegion =
    REGION_OPTIONS.find((region) =>
      isIndia ? region.code === "IN" : region.code === "AE"
    ) || REGION_OPTIONS[0];
  const menuItems = filterNavItemsForIndia(MENU_ITEMS, isIndia);

  useEffect(() => {
    const syncHeaderOffset = () => {
      const navbar = document.getElementById("site-navbar");
      if (!navbar) return;
      document.documentElement.style.setProperty(
        "--site-header-offset",
        `${Math.ceil(navbar.getBoundingClientRect().height)}px`
      );
    };

    syncHeaderOffset();
    window.addEventListener("resize", syncHeaderOffset);

    let observer;
    if (typeof ResizeObserver !== "undefined") {
      const navbar = document.getElementById("site-navbar");
      if (navbar) {
        observer = new ResizeObserver(syncHeaderOffset);
        observer.observe(navbar);
      }
    }

    return () => {
      window.removeEventListener("resize", syncHeaderOffset);
      observer?.disconnect();
    };
  }, [showModuleNav]);

  useBodyScrollLock(menuOpen);

  useEffect(() => {
    if (!menuOpen) return undefined;

    const onKeyDown = (event) => {
      if (event.key === "Escape") setMenuOpen(false);
    };

    document.addEventListener("keydown", onKeyDown);

    return () => document.removeEventListener("keydown", onKeyDown);
  }, [menuOpen]);

  useEffect(() => {
    if (!accountPopoverOpen) return undefined;

    const onKeyDown = (event) => {
      if (event.key === "Escape") setAccountPopoverOpen(false);
    };

    const onPointerDown = (event) => {
      if (!accountMenuRef.current?.contains(event.target)) {
        setAccountPopoverOpen(false);
      }
    };

    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("mousedown", onPointerDown);

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("mousedown", onPointerDown);
    };
  }, [accountPopoverOpen]);

  useEffect(() => {
    if (!localeOpen) return undefined;

    const onKeyDown = (event) => {
      if (event.key === "Escape") setLocaleOpen(false);
    };

    const onPointerDown = (event) => {
      if (!localeRef.current?.contains(event.target)) {
        setLocaleOpen(false);
      }
    };

    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("mousedown", onPointerDown);

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("mousedown", onPointerDown);
    };
  }, [localeOpen]);

  const closeMenu = () => setMenuOpen(false);

  const handleRegionChange = (regionCode) => {
    if ((regionCode === "IN" && isIndia) || (regionCode === "AE" && !isIndia)) {
      setLocaleOpen(false);
      return;
    }

    setRegionPreference(regionCode);
    setLocaleOpen(false);
    window.location.href = buildCountrySwitchEntryUrl(regionCode);
  };

  const handleAccountItemClick = (item) => {
    if (item?.action === "logout" || item?.id === "logout") {
      setAccountPopoverOpen(false);
      setLogoutOpen(true);
      return;
    }

    setAccountPopoverOpen(false);
  };

  const handleLogout = () => {
    setLoggingOut(true);
    clearAuthSession();
    window.location.href = getRegionalHomeHref();
  };

  const openDrawer = () => {
    setAccountPopoverOpen(false);
    setMenuOpen(true);
  };

  const openLoginModal = () => {
    closeMenu();
    openAuthModal({ mode: "login" });
  };

  return (
    <>
      <header
        id="site-navbar"
        className={`standard-padding ${styles.navbar} ${showModuleNav ? styles.navbarWithModules : ""}`}
      >
        <div
          className={`${styles.navbarTopRow} ${showModuleNav ? styles.navbarTopRowWithModules : ""
            }`}
        >
          <a href={withRegionPath("/holiday")} className={styles.brand} aria-label="Next Holidays home">
            <Image
              src={isIndia ? "/images/IndiaLogoR.svg" : "/images/logo.png"}
              alt="Next Holidays"
              width={180}
              height={44}
              className={styles.logoImg}
              priority
            />
          </a>

          {showModuleNav ? (
            <div className={styles.moduleNavDesktopWrap}>
              {!isIndianUser() &&
                <ModuleNav variant="navbar" />
              }
            </div>
          ) : null}

          <div className={styles.actions}>
            <NotificationSafe>
              <NotificationDropdown />
            </NotificationSafe>

            <div className={styles.localeWrap} ref={localeRef}>
              <button
                type="button"
                className={styles.locale}
                aria-label="Select country"
                aria-expanded={localeOpen}
                aria-haspopup="listbox"
                onClick={() => setLocaleOpen((prev) => !prev)}
              >
                <Image
                  src={selectedRegion.flag}
                  alt=""
                  width={20}
                  height={20}
                  className={styles.flag}
                  unoptimized
                />
                <span>{selectedRegion.label}</span>
                <FaChevronDown size={13}
                  className={`${styles.localeChevron} ${localeOpen ? styles.localeChevronOpen : ""
                    }`}
                />
              </button>

              {localeOpen ? (
                <div className={styles.localeDropdown} role="listbox">
                  {REGION_OPTIONS.filter((region) => region.code !== selectedRegion.code).map(
                    (region) => (
                      <button
                        key={region.code}
                        type="button"
                        role="option"
                        className={styles.localeOption}
                        onClick={() => handleRegionChange(region.code)}
                      >
                        <Image
                          src={region.flag}
                          alt=""
                          width={20}
                          height={20}
                          className={styles.flag}
                          unoptimized
                        />
                        <span>
                          {region.label} | {region.currency}
                        </span>
                      </button>
                    )
                  )}
                </div>
              ) : null}
            </div>

            {loggedIn ? (
              <div className={styles.userMenuWrap} ref={accountMenuRef}>
                <button
                  type="button"
                  className={styles.userMenuTrigger}
                  aria-expanded={accountPopoverOpen}
                  aria-haspopup="true"
                  onClick={() => setAccountPopoverOpen((prev) => !prev)}
                >
                  <span>{loginLabel}</span>
                  <FaChevronDown size={13}
                    className={`${styles.userMenuChevron} ${accountPopoverOpen ? styles.userMenuChevronOpen : ""
                      }`}
                  />
                </button>

                {accountPopoverOpen ? (
                  <div className={styles.userMenuPopover}>
                    <AccountMenu variant="popover" onItemClick={handleAccountItemClick} />
                  </div>
                ) : null}
              </div>
            ) : (
              <Link
                href={withRegionPath("/login")}
                className={styles.loginBtn}
                onClick={(event) => {
                  event.preventDefault();
                  openAuthModal({ mode: "login" });
                }}
              >
                {loginLabel}
              </Link>
            )}

            <button
              type="button"
              className={styles.menuBtn}
              aria-label="Open menu"
              aria-expanded={menuOpen}
              aria-controls="main-menu-drawer"
              onClick={openDrawer}
            >
              <IoMenu size={22} />
            </button>
          </div>
        </div>
      </header>

      <div
        className={`${styles.overlay} ${menuOpen ? styles.overlayVisible : ""}`}
        onClick={closeMenu}
        aria-hidden={!menuOpen}
      />

      <aside
        id="main-menu-drawer"
        className={`${styles.drawer} ${menuOpen ? styles.drawerOpen : ""}`}
        aria-hidden={!menuOpen}
        aria-label="Main menu"
      >
        <nav className={styles.drawerNav}>
          {loggedIn ? (
            <Link
              href={withRegionPath("/profile")}
              className={styles.drawerProfileCard}
              onClick={closeMenu}
            >
              <span className={styles.drawerProfileIcon}>
                <FaUser size={18} />
              </span>
              <span className={styles.drawerProfileCopy}>
                <span className={styles.drawerProfileTitle}>My Profile</span>
                <span className={styles.drawerProfileSubtitle}>
                  View and update your account
                </span>
              </span>
              <FaChevronRight className={styles.drawerProfileChevron} aria-hidden="true" />
            </Link>
          ) : (
            <button
              type="button"
              className={styles.drawerProfileCard}
              onClick={openLoginModal}
            >
              <span className={styles.drawerProfileIcon}>
                <FaUser size={20} />
              </span>
              <span className={styles.drawerProfileCopy}>
                <span className={styles.drawerProfileTitle}>Login/Signup</span>
                <span className={styles.drawerProfileSubtitle}>
                  Login for best deals &amp; offers
                </span>
              </span>
              <FaChevronRight className={styles.drawerProfileChevron} aria-hidden="true" />
            </button>
          )}

          <ul className={styles.menuList}>
            {menuItems.map((item) => (
              <li key={item.label}>
                <a href={withRegionPath(item.href)} className={styles.menuLink} onClick={closeMenu}>
                  <span>{item.label}</span>
                  <span className={styles.menuArrow}>
                    <MdOutlineArrowOutward size={20} />
                  </span>
                </a>
              </li>
            ))}
          </ul>
        </nav>

        <div className={styles.drawerFooter}>
          <div className={styles.drawerFooterAwards}>
            {DRAWER_AWARDS.map((award) => (
              <Image
                key={award.label}
                src={award.src}
                alt={award.label}
                width={28}
                height={28}
                className={styles.drawerFooterAward}
              />
            ))}
          </div>
          <span className={styles.drawerFooterRating}>4.2 ★ Rated</span>
        </div>
      </aside>

      <LogoutModal
        open={logoutOpen}
        loading={loggingOut}
        onClose={() => {
          if (!loggingOut) setLogoutOpen(false);
        }}
        onConfirm={handleLogout}
      />
    </>
  );
}
